import {
  DragEndEvent,
  DragMoveEvent,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useCallback, useState } from 'react';

import { AssetCardProps } from '../AssetCard';
import { ImageCardProps } from '../Card';
import { SVGCardProps } from '../SVGCard';

export interface DroppableItem {
  source: string | string[];
  title: string;
  details?: Record<string, unknown>;
}

export interface DraggableData {
  active: {
    data: {
      current: DroppableItem;
    };
  };
  over: unknown | null;
}

export interface useDraggableProps {
  onClick: AssetCardProps['onClick'] | SVGCardProps['onClick'] | ImageCardProps['onClick'];
  onDragOver: ({
    activatorEvent,
    active,
    delta,
    over,
  }: {
    activatorEvent: DragEndEvent['activatorEvent'];
    active: DragEndEvent['active'];
    delta: DragEndEvent['delta'];
    over: DragEndEvent['over'];
  }) => void;
  onDragMove: ({
    activatorEvent,
    active,
    delta,
    over,
  }: {
    activatorEvent: DragMoveEvent['activatorEvent'];
    active: DragMoveEvent['active'];
    delta: DragMoveEvent['delta'];
    over: DragMoveEvent['over'];
  }) => void;
}

export function useDraggable({ onClick, onDragOver, onDragMove }: useDraggableProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [activeItem, setActiveItem] = useState<DroppableItem | null>(null);
  const handleDragStart = useCallback(
    ({ active }: DragStartEvent) => {
      setIsDragging(true);
      setActiveItem(active.data.current as DroppableItem);

      onClick?.({
        action: 'start-dragging',
        source: '',
        details: {},
      });
    },
    [onClick]
  );

  const handleDragEnd = useCallback(
    ({ activatorEvent, active, delta, over }: DragEndEvent) => {
      setActiveItem(null);
      setIsDragging(false);
      onDragOver({ activatorEvent, active, delta, over });

      onClick?.({
        action: 'stop-dragging',
        source: '',
        details: {},
      });
    },
    [onDragOver, onClick]
  );

  const handleDragMove = useCallback(
    ({ activatorEvent, active, delta, over }: DragMoveEvent) => {
      onDragMove({ activatorEvent, active, delta, over });
    },
    [onDragMove]
  );

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 5,
    },
  });

  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 5,
      tolerance: 5,
    },
  });

  const sensors = useSensors(mouseSensor, touchSensor);

  return {
    isDragging,
    activeItem,
    handleDragStart,
    handleDragEnd,
    handleDragMove,
    sensors,
  };
}
